import React, { useEffect, useState, useRef } from 'react';
import { Container } from './styled';
import { useParams } from 'react-router-dom';
import { validateBookingPayment } from 'api';
import { 
  Button, 
  Box, 
  LinearProgress, 
  Grid, 
  Card, 
  CardContent, 
  Link,
  Fade,
  Paper,
  Divider,
  useTheme
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmailIcon from '@mui/icons-material/Email';

const BookingResult = () => {
  const params = useParams();
  const { sessionId } = params;
  const [validationResult, setValidationResult] = useState({ result: 'loading' });
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Use a ref to track if we've already made the API call
  const hasValidatedRef = useRef(false);

  const navigateMenu = () => {
    navigate(`/bookings`);
  };

  useEffect(() => {
    // Only make the API call if we haven't already done so
    if (!hasValidatedRef.current && sessionId) {
      hasValidatedRef.current = true;
      validateBookingPayment(sessionId)
        .then(r => {
          setValidationResult(r);
        })
        .catch(() => {
          setValidationResult({ result: 'error', message: 'Failed to validate payment' });
        });
    }
  }, [sessionId]);

  const StatusIcon = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      mb: 3,
      animation: 'bounce 2s infinite'
    }}>
      {validationResult.status === "AC" ? (
        <CheckCircleIcon sx={{ 
          fontSize: 60, 
          color: 'success.main',
          '@keyframes bounce': {
            '0%, 100%': { transform: 'translateY(0)' },
            '50%': { transform: 'translateY(-10px)' }
          }
        }} />
      ) : (
        <AccessTimeIcon sx={{ 
          fontSize: 60, 
          color: 'warning.main',
          '@keyframes bounce': {
            '0%, 100%': { transform: 'translateY(0)' },
            '50%': { transform: 'translateY(-10px)' }
          }
        }} />
      )}
    </Box>
  );

  return (
    <Container>
      <Fade in={true} timeout={1000}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8}>
            <Card 
              elevation={3}
              sx={{ 
                marginTop: '50px',
                borderRadius: 2,
                background: theme.palette.background.paper,
                position: 'relative',
                overflow: 'visible'
              }}
            >
              <Paper 
                elevation={4}
                sx={{
                  position: 'absolute',
                  top: -25,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '15px 30px',
                  borderRadius: '25px',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white'
                }}
              >
                <Typography variant="h6">
                  Hive B Ltd
                </Typography>
              </Paper>

              <CardContent sx={{ pt: 7 }}>
                {validationResult.result === 'loading' && (
                  <Box sx={{ width: '50%', margin: '40px auto' }}>
                    <LinearProgress color="primary" />
                  </Box>
                )}

                {validationResult.result === 'ok' && (
                  <Fade in={true} timeout={1500}>
                    <Grid container spacing={4}>
                      <Grid item xs={12}>
                        <StatusIcon />
                        <Typography variant="h4" align="center" gutterBottom>
                          {validationResult.status === "AC" ? 
                            "Booking Confirmed!" : 
                            "Booking Pending Confirmation"}
                        </Typography>
                        <Typography 
                          variant="subtitle1" 
                          align="center"
                          sx={{ color: 'text.secondary' }}
                        >
                          Reference: <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {validationResult.code}
                          </Box>
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box 
                          component="img"
                          src="https://workersleague.com/wp-content/uploads/2021/04/Bonhill-CW_DSC_5703.jpg"
                          sx={{
                            width: '100%',
                            borderRadius: 2,
                            boxShadow: 3,
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.02)'
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOnIcon color="primary" />
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">Venue</Typography>
                                <Typography variant="body1">{validationResult.venue}</Typography>
                                <Typography variant="body2" color="text.secondary">{validationResult.address}</Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarTodayIcon color="primary" />
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
                                <Typography variant="body1">{validationResult.day}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {validationResult.start} - {validationResult.end}
                                </Typography>
                              </Box>
                            </Box>

                            <Divider />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle1">Price</Typography>
                              <Typography variant="h6" color="primary.main">£{validationResult?.price}</Typography>                              
                            </Box>

                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary">Access Instructions</Typography>
                              <Box 
                                sx={{ mt: 1 }}
                                dangerouslySetInnerHTML={{ __html: validationResult?.access }}
                              />
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper 
                          elevation={1}
                          sx={{ 
                            p: 2, 
                            backgroundColor: theme.palette.green.sad,
                            color: 'info.contrastText',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <EmailIcon />
                          <Typography variant="body2">
                            {validationResult?.status === "AC" 
                              ? "Check your email for booking details." 
                              : "You will receive an email with your booking confirmation."} 
                            Contact us at <Link href="mailto:hello@hivespace.co" sx={{ color: 'inherit', fontWeight: 'bold' }}>
                              hello@hivespace.co
                            </Link>
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sx={{ textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={navigateMenu}
                          sx={{ 
                            minWidth: 200,
                            borderRadius: 50,
                            textTransform: 'none',
                            boxShadow: 2
                          }}
                        >
                          View All Bookings
                        </Button>
                      </Grid>
                    </Grid>
                  </Fade>
                )}

                {validationResult.result === 'existing' && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                      This booking has already been confirmed
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Please contact us at <Link href="mailto:hello@hivespace.co">hello@hivespace.co</Link>
                    </Typography>
                  </Box>
                )}
                
                {validationResult.result === 'error' && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                      Error validating payment
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Please contact us at <Link href="mailto:hello@hivespace.co">hello@hivespace.co</Link>
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>
    </Container>
  );
};

export default BookingResult;